import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Cpu, Lock, Unlock, Settings, Zap, Target, TrendingUp, Clock, MapPin } from 'lucide-react';

interface SliderConfig {
  id: string;
  label: string;
  icon: React.ElementType;
  value: number;
  targetValue: number;
  displayValue: string;
  color: string;
}

export const CalibrationInterface: React.FC = () => {
  const [isCalibrating, setIsCalibrating] = useState(true);
  const [sliders, setSliders] = useState<SliderConfig[]>([
    { id: 'capital', label: 'Capital Range', icon: TrendingUp, value: 0, targetValue: 65, displayValue: '£250k - £500k', color: 'from-emerald-400 to-emerald-500' },
    { id: 'returns', label: 'Target Returns', icon: Target, value: 0, targetValue: 78, displayValue: 'Cost certainty', color: 'from-luxury-gold to-luxury-gold-light' },
    { id: 'timeline', label: 'Timeline', icon: Clock, value: 0, targetValue: 55, displayValue: '6-12 months', color: 'from-blue-400 to-blue-500' },
    { id: 'location', label: 'Location Focus', icon: MapPin, value: 0, targetValue: 85, displayValue: 'UK Regional', color: 'from-purple-400 to-purple-500' },
  ]);

  // Animate sliders to their target values
  useEffect(() => {
    const timer = setTimeout(() => {
      setSliders(prev => prev.map((slider, idx) => ({
        ...slider,
        value: slider.targetValue,
      })));
    }, 800);

    const calibrationTimer = setTimeout(() => {
      setIsCalibrating(false);
    }, 2500);

    return () => {
      clearTimeout(timer);
      clearTimeout(calibrationTimer);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.3 }}
      className="relative w-full max-w-sm sm:max-w-md"
    >
      {/* Glow effect behind card */}
      <div className="absolute inset-0 bg-gradient-to-br from-luxury-gold/20 via-transparent to-luxury-gold/10 rounded-3xl blur-xl transform scale-105" />

      {/* Main Card */}
      <div className="relative bg-white/80 dark:bg-[#1a2744]/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-luxury-gold/20 shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-luxury-gold/10 bg-gradient-to-r from-luxury-gold/5 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-luxury-gold/20 to-luxury-gold/10 flex items-center justify-center border border-luxury-gold/20">
                <Cpu className="w-4 h-4 sm:w-5 sm:h-5 text-luxury-gold" />
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-semibold text-luxury-text dark:text-luxury-text-dark">
                  Partner Deal Engine
                </h3>
                <div className="flex items-center gap-1.5">
                  <motion.div
                    animate={{ scale: isCalibrating ? [1, 1.2, 1] : 1 }}
                    transition={{ duration: 1, repeat: isCalibrating ? Infinity : 0 }}
                    className={`w-1.5 h-1.5 rounded-full ${isCalibrating ? 'bg-amber-400' : 'bg-emerald-400'}`}
                  />
                  <span className="text-[10px] sm:text-xs text-luxury-text-muted dark:text-luxury-text-muted-dark">
                    {isCalibrating ? 'Calibrating...' : 'Ready'}
                  </span>
                </div>
              </div>
            </div>

            {/* Lock Status */}
            <motion.div
              animate={{ rotate: isCalibrating ? 0 : 0 }}
              className="flex items-center gap-2"
            >
              <motion.div
                initial={{ scale: 1 }}
                animate={{ scale: isCalibrating ? 1 : [1, 1.2, 1] }}
                transition={{ duration: 0.5 }}
              >
                {isCalibrating ? (
                  <Lock className="w-4 h-4 text-amber-500" />
                ) : (
                  <Unlock className="w-4 h-4 text-emerald-500" />
                )}
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Sliders Section */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4 sm:space-y-5">
          {sliders.map((slider, idx) => {
            const Icon = slider.icon;
            return (
              <motion.div
                key={slider.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + idx * 0.15 }}
                className="space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-luxury-gold" />
                    <span className="text-xs sm:text-sm font-medium text-luxury-text dark:text-luxury-text-dark">
                      {slider.label}
                    </span>
                  </div>
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: slider.value > 0 ? 1 : 0.5 }}
                    className="text-xs font-semibold text-luxury-gold"
                  >
                    {slider.displayValue}
                  </motion.span>
                </div>

                {/* Slider Track */}
                <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${slider.value}%` }}
                    transition={{ duration: 1.5, delay: 0.5 + idx * 0.2, ease: "easeOut" }}
                    className={`absolute inset-y-0 left-0 bg-gradient-to-r ${slider.color} rounded-full`}
                  />
                  {/* Shimmer effect */}
                  <motion.div
                    initial={{ x: '-100%' }}
                    animate={{ x: '200%' }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                    className="absolute inset-y-0 w-1/4 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Status Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-luxury-gold/10 bg-gradient-to-r from-transparent to-luxury-gold/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <motion.div
                animate={{ rotate: isCalibrating ? 360 : 0 }}
                transition={{ duration: 2, repeat: isCalibrating ? Infinity : 0, ease: "linear" }}
              >
                <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-luxury-gold" />
              </motion.div>
              <span className="text-[10px] sm:text-xs text-luxury-text-muted dark:text-luxury-text-muted-dark">
                {isCalibrating ? 'Configuring sourcing parameters...' : 'System aligned to your criteria'}
              </span>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: isCalibrating ? 0 : 1 }}
              transition={{ delay: 2.5 }}
              className="flex items-center gap-1"
            >
              <Zap className="w-3 h-3 text-emerald-500" />
              <span className="text-[10px] sm:text-xs font-medium text-emerald-500">Active</span>
            </motion.div>
          </div>
        </div>

        {/* Floating particles animation */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-luxury-gold/40 rounded-full"
              initial={{
                x: Math.random() * 100 + '%',
                y: '100%',
                opacity: 0
              }}
              animate={{
                y: '-20%',
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeOut"
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom accent line */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-0.5 bg-gradient-to-r from-transparent via-luxury-gold/50 to-transparent"
      />
    </motion.div>
  );
};

export default CalibrationInterface;
